import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Dropdown, Spinner, Alert, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber } from "../utils/formatters";
import './Produits.css';

export default function Produits() {
    const navigate = useNavigate();
    const [produits, setProduits] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [categorieFilter, setCategorieFilter] = useState('');
    const [stats, setStats] = useState(null);

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchProduits();
        fetchCategories();
        fetchStats();
    }, [categorieFilter]);

    const fetchProduits = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/produits.php';
            if (categorieFilter) {
                url += `?categorie=${categorieFilter}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduits(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/categories.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement catégories", err);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/produits.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStockStatus = (produit) => {
        if (!produit.stock_reel || produit.stock_reel <= 0) {
            return { label: 'Rupture', color: 'danger', icon: 'ri-close-circle-line' };
        }
        if (produit.stock_reel <= produit.seuil_alerte) {
            return { label: 'Critique', color: 'warning', icon: 'ri-alert-line' };
        }
        return { label: 'Normal', color: 'success', icon: 'ri-check-line' };
    };

    const filteredProduits = produits.filter(p => 
        p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
        p.designation?.toLowerCase().includes(filterText.toLowerCase()) ||
        p.code_barre?.toLowerCase().includes(filterText.toLowerCase())
    );

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProduits.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProduits.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <ol className="breadcrumb fs-sm mb-1">
                                        <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock">Stock</Link></li>
                                        <li className="breadcrumb-item active">Produits</li>
                                    </ol>
                                    <h4 className="main-title mb-0">
                                        <i className="ri-suitcase-2-line text-primary me-2"></i>
                                        Gestion des produits
                                    </h4>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="primary" 
                                        onClick={() => navigate('/stock/produitsAjouter')}
                                    >
                                        <i className="ri-add-line me-2"></i>
                                        Nouveau produit
                                    </Button>
                                    <Button 
                                        variant="outline-primary" 
                                        onClick={() => navigate('/stock/produits/import')}
                                    >
                                        <i className="ri-upload-cloud-2-line me-2"></i>
                                        Importer
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                    {stats && (
                        <Row className="g-3 mb-4 stats-row">
                            {/* Total produits */}
                            <Col sm={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3 d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '56px', 
                                                height: '56px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                                boxShadow: '0 8px 16px rgba(67, 97, 238, 0.15)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-suitcase-2-line text-primary" style={{ fontSize: '28px', lineHeight: 1 }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Total produits
                                            </span>
                                            <div className="d-flex align-items-baseline gap-2">
                                                <h3 className="stat-value text-primary mb-0 fw-bold" style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                                                    {stats.total || 0}
                                                </h3>
                                            </div>
                                            <small className="stat-trend text-primary d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-stack-line"></i>
                                                <span className="text-truncate">en stock</span>
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Rupture */}
                            <Col sm={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3 d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '56px', 
                                                height: '56px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.15), rgba(187, 45, 59, 0.15))',
                                                boxShadow: '0 8px 16px rgba(220, 53, 69, 0.15)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-close-circle-line text-danger" style={{ fontSize: '28px', lineHeight: 1 }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Rupture
                                            </span>
                                            <div className="d-flex align-items-baseline gap-2">
                                                <h3 className="stat-value text-danger mb-0 fw-bold" style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                                                    {stats.rupture || 0}
                                                </h3>
                                                <span className="badge bg-danger bg-opacity-10 text-white px-2 py-1 rounded-pill">
                                                    <i className="ri-error-warning-line me-1"></i>
                                                    Alerte
                                                </span>
                                            </div>
                                            <small className="stat-trend text-danger d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-alert-line"></i>
                                                <span className="text-truncate">à réapprovisionner</span>
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Stock critique */}
                            <Col sm={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3 d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '56px', 
                                                height: '56px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.15))',
                                                boxShadow: '0 8px 16px rgba(245, 158, 11, 0.15)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-alert-line text-warning" style={{ fontSize: '28px', lineHeight: 1 }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Stock critique
                                            </span>
                                            <div className="d-flex align-items-baseline gap-2">
                                                <h3 className="stat-value text-warning mb-0 fw-bold" style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                                                    {stats.alerte || 0}
                                                </h3>
                                                <span className="badge bg-warning bg-opacity-10 text-white px-2 py-1 rounded-pill">
                                                    <i className="ri-alarm-warning-line me-1"></i>
                                                    Seuil bas
                                                </span>
                                            </div>
                                            <small className="stat-trend text-warning d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-time-line"></i>
                                                <span className="text-truncate">réapprovisionnement conseillé</span>
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Valeur stock */}
                            <Col sm={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3 d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '56px', 
                                                height: '56px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))',
                                                boxShadow: '0 8px 16px rgba(16, 185, 129, 0.15)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-money-euro-circle-line text-success" style={{ fontSize: '28px', lineHeight: 1 }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Valeur stock
                                            </span>
                                            <div className="d-flex align-items-baseline gap-2">
                                                <h5 className="stat-value text-success mb-0 fw-bold" style={{ fontSize: '1rem', lineHeight: 1.2 }}>
                                                    {formatCurrency(stats.valeur?.valeur_achat || 0)}
                                                </h5>
                                            </div>
                                            <small className="stat-trend text-success d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-arrow-up-line"></i>
                                                <span className="text-truncate">prix d'achat</span>
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={4}>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <i className="ri-search-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher référence, désignation..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={categorieFilter}
                                        onChange={(e) => setCategorieFilter(e.target.value)}
                                    >
                                        <option value="">Toutes catégories</option>
                                        {categories.map(cat => (
                                            <option key={cat.id_categorie} value={cat.id_categorie}>
                                                {cat.nom_categorie}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={5} className="text-end">
                                    <span className="text-muted">
                                        {filteredProduits.length} produit(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des produits avec pagination */}
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Référence</th>
                                            <th style={{minWidth:'230px'}}>Désignation</th>
                                            <th>Catégorie</th>
                                           
                                            <th style={{minWidth:'140px'}} className="text-start">Prix Vente</th>
                                            <th className="text-center">Stock</th>
                                            
                                            <th>Statut</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="10" className="text-center py-4">
                                                    <Spinner animation="border" variant="primary" />
                                                </td>
                                            </tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((produit) => {
                                                const stockStatus = getStockStatus(produit);
                                                return (
                                                    <tr key={produit.id_produit} className="produit-row">
                                                        <td>
                                                            <Badge bg="light" text="dark" className="font-mono">
                                                                {produit.reference}
                                                            </Badge>
                                                            {produit.code_barre && (
                                                                <small className="d-block text-muted">{produit.code_barre}</small>
                                                            )}
                                                        </td>
                                                        <td className="fw-semibold">
                                                           {produit.designation}
                                                           
                                                        </td>
                                                        <td>{produit.nom_categorie || '-'}</td>
                                                       

                                                        <td className="text-start fw-bold text-primary">{formatCurrency(produit.prix_vente_ht)}</td>
                                                        <td className="text-center">
                                                            <span className={`fw-bold ${stockStatus.color === 'danger' ? 'text-danger' : stockStatus.color === 'warning' ? 'text-warning' : ''}`}>
                                                                {formatNumber(produit.stock_reel || 0)}
                                                            </span>
                                                            {produit.stock_reel <= produit.seuil_alerte && (
                                                                <i className="ri-alert-line text-warning ms-1" title="Stock critique"></i>
                                                            )}
                                                        </td>
                                                        

                                                        <td>
                                                            <Badge bg={stockStatus.color} className="px-3">
                                                                <i className={`${stockStatus.icon} me-1`}></i>
                                                                {stockStatus.label}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm"
                                                                    onClick={() => navigate(`/stock/produits/${produit.id_produit}`)}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </Button>
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    onClick={() => navigate(`/stock/produits/modifier/${produit.id_produit}`)}
                                                                >
                                                                    <i className="ri-pencil-line"></i>
                                                                </Button>
                                                                <Button 
                                                                    variant="outline-success" 
                                                                    size="sm"
                                                                    onClick={() => navigate(`/stock/mouvements/ajouter/${produit.id_produit}`)}
                                                                    title="Ajouter au stock"
                                                                >
                                                                    <i className="ri-add-box-line"></i>
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="10" className="text-center py-4">
                                                    <i className="ri-suitcase-2-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucun produit trouvé</h5>
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={() => navigate('/stock/produits/ajouter')}
                                                        className="mt-2"
                                                    >
                                                        <i className="ri-add-line me-2"></i>
                                                        Ajouter un produit
                                                    </Button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredProduits.length)} sur {filteredProduits.length} produits
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                .stat-icon {
                    width: 45px;
                    height: 45px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                }
                .bg-primary-soft { background: rgba(13, 110, 253, 0.1); }
                .bg-success-soft { background: rgba(25, 135, 84, 0.1); }
                .bg-warning-soft { background: rgba(255, 193, 7, 0.1); }
                .bg-danger-soft { background: rgba(220, 53, 69, 0.1); }
                .font-mono {
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
                }
                .produit-row {
                    transition: all 0.2s ease;
                }
                .produit-row:hover {
                    background: rgba(13, 110, 253, 0.05);
                }

                .stats-row .stat-card {
                    transition: all 0.3s ease;
                    border-radius: 16px;
                }

                .stats-row .stat-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                }

                .stat-icon-wrapper {
                    transition: transform 0.3s ease;
                }

                .stat-card:hover .stat-icon-wrapper {
                    transform: scale(1.1);
                }

                .stat-label {
                    font-size: 0.7rem;
                    letter-spacing: 0.5px;
                }

                .stat-trend {
                    font-size: 0.75rem;
                }

                /* Styles pour la pagination */
                .pagination {
                    gap: 5px;
                }
                
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                    box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                }
                
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
            `}</style>
        </React.Fragment>
    );
}